import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Input,
  Label,
  Button,
  Spinner,
  Alert,
} from "reactstrap";

import CountUp from "react-countup";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import api from "../../../utills/api";

const BookingLogs = () => {
  document.title = "HMS | Booking Logs";

  /* =========================================================
     STATE
  ========================================================= */

  const [widgets, setWidgets] = useState({
    bookingsReceived: 0,
    imported: 0,
    failed: 0,
    cancelled: 0,
  });

  const [bookingLogs, setBookingLogs] = useState([]);
  const [failedLogs, setFailedLogs] = useState([]);

  const [filters, setFilters] = useState({
    date: "",
    channel: "ALL",
    status: "ALL",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    date: "",
    channel: "ALL",
    status: "ALL",
  });

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  /* =========================================================
     HELPERS
  ========================================================= */

  const safeArray = (value) => {
    return Array.isArray(value) ? value : [];
  };

  const safeString = (value, fallback = "-") => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return fallback;
    }

    return String(value);
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const normalizeStatus = (value) => {
    if (!value) return "";

    return String(value)
      .trim()
      .toUpperCase();
  };

  const normalizeChannel = (value) => {
    if (!value) return "";

    const channel = String(value)
      .trim()
      .toUpperCase();

    /*
      Keep compatibility with existing backend values.

      BOOKING
      BOOKINGCOM
      BOOKING.COM
      all represent Booking.com
    */

    if (
      channel === "BOOKING" ||
      channel === "BOOKINGCOM" ||
      channel === "BOOKING.COM"
    ) {
      return "BOOKINGCOM";
    }

    return channel;
  };

  const getGuestName = (row) => {
    if (typeof row.guest === "string") {
      return row.guest;
    }

    if (row.guest?.name) {
      return row.guest.name;
    }

    if (row.guest?.fullName) {
      return row.guest.fullName;
    }

    if (row.guestName) {
      return row.guestName;
    }

    if (row.customerName) {
      return row.customerName;
    }

    return "-";
  };

  const getRoomTypeName = (row) => {
    if (typeof row.roomType === "string") {
      return row.roomType;
    }

    if (row.roomType?.name) {
      return row.roomType.name;
    }

    if (row.roomType?.roomTypeName) {
      return row.roomType.roomTypeName;
    }

    if (row.roomTypeName) {
      return row.roomTypeName;
    }

    return "-";
  };

  /* =========================================================
     LOAD BOOKING LOGS
  ========================================================= */

  const loadLogs = useCallback(
    async (customFilters = appliedFilters) => {
      try {
        setError("");

        setLoading(true);

        const params = {};

        /*
          Only send filters that are actually selected.
          This prevents unnecessary ALL/date parameters.
        */

        if (customFilters.date) {
          params.date = customFilters.date;
        }

        if (
          customFilters.channel &&
          customFilters.channel !== "ALL"
        ) {
          params.channel =
            customFilters.channel;
        }

        if (
          customFilters.status &&
          customFilters.status !== "ALL"
        ) {
          params.status =
            customFilters.status;
        }

        const response = await api.get(
          "/bookings/logs",
          {
            params,
          }
        );

        const data = response?.data || {};

        /*
          Backend expected response:

          {
            bookingLogs: [],
            failedLogs: [],
            widgets: {
              bookingsReceived,
              imported,
              failed,
              cancelled
            }
          }
        */

        setBookingLogs(
          safeArray(data.bookingLogs)
        );

        setFailedLogs(
          safeArray(data.failedLogs)
        );

        setWidgets({
          bookingsReceived:
            Number(
              data.widgets
                ?.bookingsReceived
            ) || 0,

          imported:
            Number(
              data.widgets?.imported
            ) || 0,

          failed:
            Number(
              data.widgets?.failed
            ) || 0,

          cancelled:
            Number(
              data.widgets?.cancelled
            ) || 0,
        });
      } catch (err) {
        console.error(
          "Booking Logs Error:",
          err
        );

        setError(
          err.response?.data?.message ||
            "Unable to load booking logs."
        );

        setBookingLogs([]);
        setFailedLogs([]);
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters]
  );

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  /* =========================================================
     APPLY FILTERS
  ========================================================= */

  const handleApplyFilters = async () => {
    setAppliedFilters({
      ...filters,
    });
  };

  /* =========================================================
     RESET FILTERS
  ========================================================= */

  const handleResetFilters = () => {
    const reset = {
      date: "",
      channel: "ALL",
      status: "ALL",
    };

    setFilters(reset);
    setAppliedFilters(reset);
  };

  /* =========================================================
     REFRESH
  ========================================================= */

  const handleRefresh = async () => {
    try {
      setRefreshing(true);

      await loadLogs(appliedFilters);
    } finally {
      setRefreshing(false);
    }
  };

  /* =========================================================
     FILTER CHANGE
  ========================================================= */

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /* =========================================================
     WIDGETS
  ========================================================= */

  const widgetConfig = [
    {
      label: "Bookings Received",
      value: widgets.bookingsReceived,
      icon: "bx bx-calendar-check",
      color: "#0d6efd",
      bgColor: "rgba(13,110,253,.12)",
    },
    {
      label: "Imported",
      value: widgets.imported,
      icon: "bx bx-check-circle",
      color: "#198754",
      bgColor: "rgba(25,135,84,.12)",
    },
    {
      label: "Failed",
      value: widgets.failed,
      icon: "bx bx-error-circle",
      color: "#dc3545",
      bgColor: "rgba(220,53,69,.12)",
    },
    {
      label: "Cancelled",
      value: widgets.cancelled,
      icon: "bx bx-x-circle",
      color: "#fd7e14",
      bgColor: "rgba(253,126,20,.12)",
    },
  ];

  /* =========================================================
     STATUS BADGES
  ========================================================= */

  const getBookingStatusColor = (value) => {
    const status =
      normalizeStatus(value);

    switch (status) {
      case "CONFIRMED":
        return "success";

      case "CANCELLED":
      case "CANCELED":
        return "danger";

      case "PENDING":
        return "warning";

      case "HOLD":
        return "info";

      default:
        return "secondary";
    }
  };

  const getSyncStatusColor = (value) => {
    const status =
      normalizeStatus(value);

    switch (status) {
      case "SYNCED":
      case "SUCCESS":
      case "IMPORTED":
        return "success";

      case "FAILED":
      case "ERROR":
        return "danger";

      case "PENDING":
      case "PROCESSING":
        return "warning";

      default:
        return "secondary";
    }
  };

  /* =========================================================
     BOOKING TABLE
  ========================================================= */

  const bookingColumns = useMemo(
    () => [
      {
        header: "Booking ID",

        accessorFn: (row) =>
          row.otaBookingId ||
          row.bookingId ||
          row.externalReservationId ||
          "-",
      },

      {
        header: "Channel",

        accessorFn: (row) =>
          normalizeChannel(
            row.channel ||
              row.channelManager
          ),

        cell: ({ getValue }) => {
          const value = getValue();

          let label = value;

          if (value === "BOOKINGCOM") {
            label = "Booking.com";
          } else if (
            value === "EXPEDIA"
          ) {
            label = "Expedia";
          } else if (
            value === "AGODA"
          ) {
            label = "Agoda";
          } else if (
            value === "AIRBNB"
          ) {
            label = "Airbnb";
          } else if (
            value === "ICAL"
          ) {
            label = "iCal";
          } else if (
            value === "MANUAL"
          ) {
            label = "Manual";
          }

          return (
            <Badge color="light" className="text-dark">
              {label || "-"}
            </Badge>
          );
        },
      },

      {
        header: "Guest",

        accessorFn: (row) =>
          getGuestName(row),
      },

      {
        header: "Room Type",

        accessorFn: (row) =>
          getRoomTypeName(row),
      },

      {
        header: "Check-In",

        accessorKey: "checkIn",

        cell: ({ getValue }) =>
          formatDate(getValue()),
      },

      {
        header: "Check-Out",

        accessorKey: "checkOut",

        cell: ({ getValue }) =>
          formatDate(getValue()),
      },

      {
        header: "Status",

        accessorKey: "status",

        cell: ({ getValue }) => {
          const value =
            getValue();

          return (
            <Badge
              color={getBookingStatusColor(
                value
              )}
            >
              {safeString(value)}
            </Badge>
          );
        },
      },

      {
        header: "Sync Status",

        accessorKey: "syncStatus",

        cell: ({ getValue }) => {
          const value =
            getValue();

          return (
            <Badge
              color={getSyncStatusColor(
                value
              )}
            >
              {safeString(value)}
            </Badge>
          );
        },
      },

      {
        header: "Received At",

        accessorKey: "receivedAt",

        cell: ({ getValue }) =>
          formatDateTime(
            getValue()
          ),
      },
    ],
    []
  );

  /* =========================================================
     FAILED LOG TABLE
  ========================================================= */

  const failedColumns = useMemo(
    () => [
      {
        header: "Booking ID",

        accessorFn: (row) =>
          row.bookingId ||
          row.otaBookingId ||
          row.externalReservationId ||
          "-",
      },

      {
        header: "Channel",

        accessorFn: (row) =>
          normalizeChannel(
            row.channel ||
              row.channelManager
          ),

        cell: ({ getValue }) => {
          const value = getValue();

          return (
            <Badge color="light" className="text-dark">
              {value === "BOOKINGCOM"
                ? "Booking.com"
                : value || "-"}
            </Badge>
          );
        },
      },

      {
        header: "Error Message",

        accessorFn: (row) =>
          row.error ||
          row.errorMessage ||
          row.message ||
          "Unknown error",

        cell: ({ getValue }) => (
          <span
            className="text-danger"
            style={{
              whiteSpace: "normal",
              maxWidth: "450px",
              display: "block",
            }}
          >
            {getValue()}
          </span>
        ),
      },

      {
        header: "Received At",

        accessorKey: "receivedAt",

        cell: ({ getValue }) =>
          formatDateTime(
            getValue()
          ),
      },
    ],
    []
  );

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Channel Manager"
          pageTitle="Booking Logs"
        />

        {/* =====================================================
            DASHBOARD WIDGETS
        ===================================================== */}

        <Row>
          {widgetConfig.map(
            (item, index) => (
              <Col
                xl={3}
                md={6}
                key={index}
              >
                <Card>
                  <CardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-uppercase text-muted fw-semibold mb-1">
                        {item.label}
                      </p>

                      <h3 className="mb-0">
                        <CountUp
                          start={0}
                          end={
                            item.value || 0
                          }
                          duration={1.5}
                        />
                      </h3>
                    </div>

                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: 10,
                        backgroundColor:
                          item.bgColor,
                      }}
                    >
                      <i
                        className={
                          item.icon
                        }
                        style={{
                          fontSize: 24,
                          color:
                            item.color,
                        }}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            )
          )}
        </Row>

        {/* =====================================================
            ERROR
        ===================================================== */}

        {error && (
          <Alert
            color="danger"
            className="d-flex justify-content-between align-items-center"
          >
            <span>
              <i className="bx bx-error-circle me-2" />
              {error}
            </span>

            <Button
              size="sm"
              color="danger"
              outline
              onClick={handleRefresh}
            >
              Retry
            </Button>
          </Alert>
        )}

        {/* =====================================================
            FILTERS
        ===================================================== */}

        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">
              Booking Filters
            </h4>

            <Button
              color="light"
              size="sm"
              onClick={handleRefresh}
              disabled={
                loading ||
                refreshing
              }
            >
              {refreshing ? (
                <>
                  <Spinner
                    size="sm"
                    className="me-2"
                  />
                  Refreshing
                </>
              ) : (
                <>
                  <i className="bx bx-refresh me-1" />
                  Refresh
                </>
              )}
            </Button>
          </CardHeader>

          <CardBody>
            <Row className="g-3">
              <Col md={3}>
                <Label>Date</Label>

                <Input
                  type="date"
                  value={
                    filters.date
                  }
                  onChange={(e) =>
                    handleFilterChange(
                      "date",
                      e.target.value
                    )
                  }
                />
              </Col>

              <Col md={3}>
                <Label>
                  Channel
                </Label>

                <Input
                  type="select"
                  value={
                    filters.channel
                  }
                  onChange={(e) =>
                    handleFilterChange(
                      "channel",
                      e.target.value
                    )
                  }
                >
                  <option value="ALL">
                    All Channels
                  </option>

                  <option value="BOOKINGCOM">
                    Booking.com
                  </option>

                  <option value="EXPEDIA">
                    Expedia
                  </option>

                  <option value="AGODA">
                    Agoda
                  </option>

                  <option value="AIRBNB">
                    Airbnb
                  </option>

                  <option value="ICAL">
                    iCal
                  </option>

                  <option value="MANUAL">
                    Manual
                  </option>
                </Input>
              </Col>

              <Col md={3}>
                <Label>
                  Booking Status
                </Label>

                <Input
                  type="select"
                  value={
                    filters.status
                  }
                  onChange={(e) =>
                    handleFilterChange(
                      "status",
                      e.target.value
                    )
                  }
                >
                  <option value="ALL">
                    All Statuses
                  </option>

                  <option value="Pending">
                    Pending
                  </option>

                  <option value="Confirmed">
                    Confirmed
                  </option>

                  <option value="Cancelled">
                    Cancelled
                  </option>

                  <option value="Hold">
                    Hold
                  </option>
                </Input>
              </Col>

              <Col
                md={3}
                className="d-flex align-items-end gap-2"
              >
                <Button
                  color="primary"
                  onClick={
                    handleApplyFilters
                  }
                  disabled={loading}
                  className="flex-grow-1"
                >
                  {loading ? (
                    <>
                      <Spinner
                        size="sm"
                        className="me-2"
                      />
                      Loading
                    </>
                  ) : (
                    <>
                      <i className="bx bx-filter-alt me-1" />
                      Apply Filters
                    </>
                  )}
                </Button>

                <Button
                  color="light"
                  onClick={
                    handleResetFilters
                  }
                  disabled={loading}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* =====================================================
            BOOKING ACTIVITY
        ===================================================== */}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="card-title mb-1">
                    OTA Booking Activity
                  </h4>

                  <p className="text-muted mb-0">
                    Incoming reservations received
                    from connected channels
                  </p>
                </div>

                <Badge color="primary">
                  {bookingLogs.length} Records
                </Badge>
              </CardHeader>

              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />

                    <div className="text-muted mt-2">
                      Loading booking logs...
                    </div>
                  </div>
                ) : bookingLogs.length === 0 ? (
                  <div className="text-center py-5">
                    <i
                      className="bx bx-calendar-x"
                      style={{
                        fontSize: 40,
                        color: "#adb5bd",
                      }}
                    />

                    <h5 className="mt-3">
                      No booking activity
                    </h5>

                    <p className="text-muted mb-0">
                      No OTA bookings were found
                      for the selected filters.
                    </p>
                  </div>
                ) : (
                  <TableContainer
                    columns={
                      bookingColumns
                    }
                    data={
                      bookingLogs
                    }
                    customPageSize={
                      10
                    }
                    isPagination
                    className="text-nowrap"
                    theadClass="table-light"
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* =====================================================
            FAILED IMPORTS
        ===================================================== */}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="card-title mb-1 text-danger">
                    Failed Booking Imports
                  </h4>

                  <p className="text-muted mb-0">
                    Reservations that could not be
                    imported into the PMS
                  </p>
                </div>

                <Badge color="danger">
                  {failedLogs.length} Failed
                </Badge>
              </CardHeader>

              <CardBody>
                {failedLogs.length === 0 ? (
                  <div className="text-center py-4">
                    <i
                      className="bx bx-check-shield"
                      style={{
                        fontSize: 40,
                        color: "#198754",
                      }}
                    />

                    <h5 className="mt-3 text-success">
                      No Failed Imports
                    </h5>

                    <p className="text-muted mb-0">
                      All booking imports are
                      currently processing normally.
                    </p>
                  </div>
                ) : (
                  <TableContainer
                    columns={
                      failedColumns
                    }
                    data={
                      failedLogs
                    }
                    customPageSize={
                      5
                    }
                    isPagination={
                      false
                    }
                    className="text-nowrap"
                    theadClass="table-light"
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BookingLogs;